import { apiClient } from './client';
import type { PageData } from './types';

const BASE = '/ads-admin/material-process';

/**
 * 必须显式覆盖实例默认值：默认头是 application/json，axios 1.x 在该头下会把 FormData
 * 序列化成 JSON；默认超时 20 秒对视频上传也不够。
 */
const MULTIPART = {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 120_000,
} as const;

/** 数值只在这里出现一次：裸的 0/1 散在组件里读不出含义，联合类型也从它派生。 */
export const JOB_STATUS = {
  PENDING: 0,
  PROCESSING: 1,
  SUCCESS: 2,
  FAILED: 3,
  CANCELED: 4,
} as const;
export const USAGE_TYPE = { DISPLAY: 1, REVIEW: 2 } as const;

export type MaterialProcessJobStatus = (typeof JOB_STATUS)[keyof typeof JOB_STATUS];
export type MaterialUsageType = (typeof USAGE_TYPE)[keyof typeof USAGE_TYPE];

export const USAGE_TYPE_LABEL: Record<MaterialUsageType, string> = {
  [USAGE_TYPE.DISPLAY]: '投放素材',
  [USAGE_TYPE.REVIEW]: '审核素材',
};

export interface Material {
  id: number;
  usageType: MaterialUsageType;
  materialName: string;
  originalFileName: string | null;
  fileSize: number | null;
  url: string | null;
  createdBy: number;
  createdByName: string | null;
  createTime: string;
  remark: string | null;
}

export interface MaterialProcessJob {
  jobId: number;
  jobName: string;
  status: MaterialProcessJobStatus;
  statusLabel: string;
  displayMaterialId: number;
  displayMaterialName: string | null;
  reviewMaterialId: number;
  reviewMaterialName: string | null;
  displayOriginalFileName: string | null;
  displayUrl: string | null;
  reviewUrl: string | null;
  /** 仅在成功时非空。 */
  outputUrl: string | null;
  errorMessage: string | null;
  createdBy: number;
  createdByName: string | null;
  createTime: string;
  finishedTime: string | null;
  remark: string | null;
}

export interface MaterialQuery {
  pageNo?: number;
  pageSize?: number;
  usageType?: MaterialUsageType;
  keyword?: string;
}

/**
 * 批量建任务的请求体。走 JSON 不走 FormData：N 个文件在这之前已经逐个调 uploadMaterial
 * 传完了，这里只带素材 ID。
 */
export interface MaterialProcessJobBatchReq {
  /** 决定建几条任务，也决定任务顺序 */
  displayMaterialIds: number[];
  /** 不足时后端按 i % M 顺序循环复用 */
  reviewMaterialIds: number[];
  /** 留空则每条任务取各自投放素材的名字 */
  jobNamePrefix?: string;
  remark?: string;
}

export interface MaterialProcessJobQuery {
  pageNo?: number;
  pageSize?: number;
  status?: MaterialProcessJobStatus;
  keyword?: string;
}

export async function getMaterialProcessPublicKey(): Promise<string> {
  const res = await apiClient.get<string>(`${BASE}/public-key`);
  return res.data;
}

/** fd 字段：file（加密后的 *.enc）、usageType、materialName?、remark? */
export async function uploadMaterial(fd: FormData): Promise<Material> {
  const res = await apiClient.post<Material>(`${BASE}/materials`, fd, MULTIPART);
  return res.data;
}

export async function listMaterials(params: MaterialQuery = {}): Promise<PageData<Material>> {
  const res = await apiClient.get<PageData<Material>>(`${BASE}/materials`, { params });
  return res.data;
}

/**
 * fd 字段：jobName、reviewMaterialId、displayMaterialId? / file?、remark?。
 * 不允许出现任何对象 key 字段，key 一律服务端生成。
 */
export async function createMaterialProcessJob(fd: FormData): Promise<MaterialProcessJob> {
  const res = await apiClient.post<MaterialProcessJob>(`${BASE}/jobs`, fd, MULTIPART);
  return res.data;
}

/** 一次建 N 条：后端在一个事务里全建或全不建，所以要么整体成功要么整体失败。 */
export async function createMaterialProcessJobsBatch(
  body: MaterialProcessJobBatchReq,
): Promise<MaterialProcessJob[]> {
  const res = await apiClient.post<MaterialProcessJob[]>(`${BASE}/jobs/batch`, body);
  return res.data;
}

export async function listMaterialProcessJobs(
  params: MaterialProcessJobQuery = {},
): Promise<PageData<MaterialProcessJob>> {
  const res = await apiClient.get<PageData<MaterialProcessJob>>(`${BASE}/jobs`, { params });
  return res.data;
}

export async function retryMaterialProcessJob(jobId: number): Promise<MaterialProcessJob> {
  const res = await apiClient.post<MaterialProcessJob>(`${BASE}/jobs/${jobId}/retry`);
  return res.data;
}

export async function cancelMaterialProcessJob(jobId: number): Promise<MaterialProcessJob> {
  const res = await apiClient.post<MaterialProcessJob>(`${BASE}/jobs/${jobId}/cancel`);
  return res.data;
}
